import { Grid, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { activityStatusById } from "features/activity/activityConsts";
import { useGetWashActivitiesQuery } from "features/activity/activitySlice";
import { useGetAllAliasesQuery } from "features/activity/activityStatusAliasSlice";
import { useGetLastCycleLotsComplianceQuery } from "features/clients/clientConfigs/washes/washProgramSlice";
import { useGetClientsQuery } from "features/clients/clientSlice";
import WidgetTitle from "features/landing/components/FleetManagementDashboard/WidgetTitle/WidgetTitle";
import { selectIsClient } from "features/user/userSlice";
import { useGetVehiclesQuery } from "features/vehicles/vehicleSlice";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import FilterService from "services/filterService";
import FormatService from "services/formatService";
import { NewWashDashboardStyle } from "../NewWashDashboardStyle";

const AssetsWashedTable = ({ lotsWithWashRule, lots, isLoadingData, filterConfig }) => {
    const classes = NewWashDashboardStyle();

    const isClient = useSelector(selectIsClient);
    const { locationFilters } = useSelector(state => state.washDashboardState);

    let endDate = new Date();
    endDate?.setHours(23, 59, 59, 999);
    let startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 1, 1);
    startDate?.setHours(0, 0, 0, 0);
    endDate = endDate?.toISOString();
    startDate = startDate?.toISOString();

    let { data: washActivities, error: washActivitiesError, isLoading: isLoadingWashActivities } = useGetWashActivitiesQuery({ startDate, endDate });
    let { data: vehiclesData, error: vehicleError, isLoading: isLoadingVehicles, refetch: refetchVehicles } = useGetVehiclesQuery({ includeHistory: false }, { skip: !washActivities?.length > 0 });
    let { data: aliases, error: aliasError, isLoading: isLoadingAlias } = useGetAllAliasesQuery();
    let { data: clients, error: clientsError, isLoading: isLoadingClients } = useGetClientsQuery();
    let { data: lastCycleComplianceForLots, error: lastCycleComplianceError, isLoading: isLoadingLastCycleCompliance } = useGetLastCycleLotsComplianceQuery(locationFilters?.clientId?.value || 0);

    washActivities = washActivities?.completedActivities || [];

    vehiclesData = vehiclesData || [];
    aliases = aliases || [];
    clients = clients || [];
    lastCycleComplianceForLots = lastCycleComplianceForLots || [];

    const lotsLookup = useMemo(() =>
        lots?.reduce((acc, lot) => { acc[lot.id] = lot; return acc; }, {}),
        [lots]
    );

    const complianceLookup = useMemo(() => {
        if (!lastCycleComplianceForLots) return {};
        return lastCycleComplianceForLots.reduce((acc, c) => {
            acc[c.lotId] = c;
            return acc;
        }, {});
    }, [lastCycleComplianceForLots]);

    var mappedData = useMemo(() => {
        return washActivities?.map(w =>
        ({
            ...w,
            washDate: FormatService.formatDateNoConversion(w.activityDateFormatted),
            lotId: lotsLookup[w.lotId]?.id || null,
            lotName: lotsLookup[w.lotId]?.name || '',
            tenantLot: lotsLookup[w.lotId]?.ownedByTenant || false,
            lotFilter: [
                ...(lotsLookup[w.lotId]?.ownedByTenant ? ['tenant'] : []),
                ...(lotsWithWashRule.some(l => l.lotId === w.lotId) ? ['washRule'] : [])
            ],
        }))?.filter(w => w.lotId != null);
    }, [washActivities, lotsLookup, isLoadingData]);

    const lotFilters = { ...locationFilters };
    if (lotFilters.lotFilter?.value === 'all') {
        lotFilters.lotFilter = { value: null };
    }

    let filteredData = FilterService.filter(lotFilters, null, mappedData, filterConfig);

    const groupedData = useMemo(() => {
        if (!filteredData || filteredData.length === 0) return [];
        const grouped = filteredData.reduce((acc, wash) => {
            const lotId = wash.lotId;
            if (!acc[lotId]) {
                acc[lotId] = {
                    lotId: lotId,
                    lotName: wash.lotName,
                    assetsWashedLastMonth: 0,
                    assetsWashedThisMonth: 0,
                    uniqueAssetsWashed: new Set(),
                };
            }
            const currentMonth = new Date().getMonth();
            const washDate = new Date(wash.activityDate);

            acc[lotId].uniqueAssetsWashed.add(wash.assetId);

            if (washDate.getMonth() === currentMonth - 1) {
                acc[lotId].assetsWashedLastMonth += 1;
            }

            else if (washDate.getMonth() === currentMonth) {
                acc[lotId].assetsWashedThisMonth += 1;
            }

            return acc;
        }, {});
        return Object.values(grouped)
            .map(lotData => ({
                ...lotData,
                uniqueAssetsWashed: lotData.uniqueAssetsWashed.size,
                lastCycleCompliance: complianceLookup[lotData.lotId]?.slaCompliance ?? 'N/A',
            }))
            .sort(({ lastCycleCompliance: a }, { lastCycleCompliance: b }) => a === 'N/A' ? 1 : b === 'N/A' ? -1 : b - a);
    }, [filteredData]);

    const mapExportData = (a => {
        return {
            ['Lot']: a.lotName,
            ['Washes last month']: a.assetsWashedLastMonth,
            ['Washes this month']: a.assetsWashedThisMonth,
            ['Unique assets washed']: a.uniqueAssetsWashed,
            ['Last cycle compliance']: a.lastCycleCompliance + (a.lastCycleCompliance > 0 ? '%' : ''),
        };
    });

    const mapExportWashes = row => {
        const data = {
            'Wash Date': row.washDate,
            'VIN': row.descriptor,
            'AssetID': row.assetId,
            'Lot': row.lotName,
            'Status': aliases?.find(a => a.activityStatusId === row.activityStatus && a.clientId === row.clientId)?.alias ?? activityStatusById?.[row.activityStatus]?.name,
        };

        if (!isClient) {
            data['Client'] = clients?.find(c => c.id == row.clientId)?.name;
            data['Cost'] = row.price
        }
        data['Charge'] = row.revenue;
        return data;
    };

    const isLoading = isLoadingWashActivities || isLoadingData || isLoadingVehicles || isLoadingAlias || isLoadingClients;

    return <Grid container direction="row">
        <Grid container direction="row" justifyContent='space-between'>
            <Grid item xs={12}>
                <WidgetTitle
                    title='Assets Washed'
                    loading={isLoading}
                    mapExportData={mapExportData}
                    dataToExport={groupedData}
                    additionalExports={[{
                        label: "Export Washes",
                        fileLabel: "Export Washes",
                        mapExportData: mapExportWashes,
                        data: filteredData,
                    }]}
                />
            </Grid>
        </Grid>
        <Grid item className={classes.tableOuterWrapper}>
            <div className={classes.tableRelativeWrapper}>
                <div className={classes.tableScrollWrapper}>
                    <Table
                        size="small"
                        aria-label="a dense table"
                        sx={{
                            borderCollapse: 'collapse',
                            minWidth: 400,
                            tableLayout: 'fixed',
                            '& th, & td': {
                                fontSize: '0.75rem',
                                padding: '4px 8px',
                                background: '#fff',
                                textAlign: 'center',
                                verticalAlign: 'middle',
                            },
                            '& th': {
                                background: '#e3f2fd',
                                fontWeight: 600,
                                paddingTop: '2px',
                                paddingBottom: '2px',
                                whiteSpace: 'normal',
                                lineHeight: 1.9,
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            },
                            '& .MuiTableRow-root.data-highlight, & .data-highlight td': {
                                backgroundColor: '#e8f5e9 !important',
                            },
                            '&::-webkit-scrollbar:horizontal': {
                                display: 'none'
                            }
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    Lot
                                </TableCell>
                                <TableCell>
                                    Washes last month
                                </TableCell>
                                <TableCell>
                                    Washes this month
                                </TableCell>
                                <TableCell>
                                    Unique assets washed
                                </TableCell>
                                <TableCell>
                                    Last cycle compliance
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groupedData?.map((lotData, idx) =>
                                <TableRow key={lotData.lotId} className={idx < 5 ? "data-highlight" : ""}>
                                    <TableCell>
                                        {lotData.lotName}
                                    </TableCell>
                                    <TableCell>
                                        {lotData.assetsWashedLastMonth}
                                    </TableCell>
                                    <TableCell>
                                        {lotData.assetsWashedThisMonth}
                                    </TableCell>
                                    <TableCell>
                                        {lotData.uniqueAssetsWashed}
                                    </TableCell>
                                    <TableCell>
                                        {lotData.lastCycleCompliance} {lotData.lastCycleCompliance > 0 ? '%' : ''}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </Grid>
    </Grid >;
}

export default AssetsWashedTable;